// src/sites/AutoMAH.jsx
import React, { useState, useEffect, useLayoutEffect } from "react";
import { Link } from "react-router-dom";
import { useI18n } from "../I18nProvider";
import CarForm from "./CarForm";
import Overlay from "./Overlay";
import GeneralForm from "./GeneralForm";
import { whatsappLink } from "../whatsapp";
import IconWhatsApp from "./IconWhatsApp";
import Fahrzeuge from "./Fahrzeuge";
import "./styles.css";

export default function AutoMAH() {
  const { t } = useI18n();

  // Das Ankauf-/Wunschauto-Formular liegt in einem Fenster ueber der Seite.
  // null = zu, sonst "purchase" oder "wish_car".
  const [formularOffen, setFormularOffen] = useState(null);
  const [anfrageAuto, setAnfrageAuto] = useState("");

  // Dreh-Karte beim Portraet (Vorderseite Foto, Rueckseite Kontakt)
  const [gedreht, setGedreht] = useState(false);

  // Canva-Pinsel + Kurve: das Hero wird einmal aufgemalt, dann bleibt es.
  const [pinselAn, setPinselAn] = useState(false);
  const [pinselFertig, setPinselFertig] = useState(false);
  useEffect(() => {
    if (
      window.matchMedia("(prefers-reduced-motion: reduce)").matches ||
      window.matchMedia("(min-width: 769px)").matches
    ) {
      setPinselFertig(true);
      return;
    }
    const start = window.setTimeout(() => setPinselAn(true), 140);
    return () => window.clearTimeout(start);
  }, []);

  // Der Knopf in der Kopfzeile sitzt in einer anderen Komponente. Statt den
  // Zustand durch die ganze App zu reichen, meldet er sich ueber ein Ereignis.
  useEffect(() => {
    const auf = (e) => {
      const d = e.detail;
      if (d && typeof d === "object") {
        setFormularOffen(d.typ || "wish_car");
        setAnfrageAuto(d.auto || "");
      } else {
        setFormularOffen(d || "purchase");
        setAnfrageAuto("");
      }
    };
    window.addEventListener("automah:formular", auf);
    return () => window.removeEventListener("automah:formular", auf);
  }, []);

  function zumFormular(typ) {
    setAnfrageAuto("");
    setFormularOffen(typ);
  }

  // Beim Klick gehen erst die Scheinwerfer an, danach oeffnet das Fenster.
  // Ohne die kurze Verzoegerung legt sich das Formular sofort darueber und
  // niemand saehe das Aufleuchten. 340 ms sind lang genug zum Sehen und kurz
  // genug, dass es nicht wie eine haengende Seite wirkt.
  const [leuchtet, setLeuchtet] = useState(null);
  const leuchtUhr = React.useRef(null);
  useEffect(() => () => window.clearTimeout(leuchtUhr.current), []);

  function aufleuchtenDann(typ) {
    setLeuchtet(typ);
    window.clearTimeout(leuchtUhr.current);
    leuchtUhr.current = window.setTimeout(() => {
      setLeuchtet(null);
      zumFormular(typ);
    }, 340);
  }

  // Abschnitte blenden sich beim Hochscrollen sanft ein. Der Startbereich
  // bleibt ausgenommen - der soll sofort dastehen, ohne Wartezeit.
  //
  // BEWUSST useLayoutEffect: mit dem normalen useEffect waeren die Abschnitte
  // fuer einen Sekundenbruchteil sichtbar, wuerden dann verschwinden und erst
  // danach einblenden - ein sichtbares Zucken. useLayoutEffect laeuft vor dem
  // Zeichnen. Ohne JavaScript bleibt alles sichtbar, es geht also nie etwas
  // verloren.
  useLayoutEffect(() => {
    const sanft = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (sanft) return;
    const ziele = document.querySelectorAll("section:not(.hero)");
    ziele.forEach((el) => el.classList.add("einblenden"));
    const beobachter = new IntersectionObserver(
      (eintraege, selbst) => {
        eintraege.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("sichtbar");
            selbst.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12 }
    );
    ziele.forEach((el) => beobachter.observe(el));
    return () => beobachter.disconnect();
  }, []);

  return (
    <>

      <style>
        {`
        /* Rechner: Foto als Flaeche hinter der Schrift, Verlauf nur so stark,
           dass Titel lesbar bleibt und das Auto in der Mitte durchscheint. */
        .hero {
          position: relative;
          margin-top: 70px;
          padding: 140px 0 80px;
          color: white;
          text-align: center;
          overflow: hidden;
          background: #0f2a46;
        }
        /* Vollbild: erste Seite = Fensterhoehe. Foto volle Breite,
           Titel oben, Knöpfe unter AUTO M.A.H., alles sichtbar. */
        @media (min-width: 769px) {
          .hero {
            width: 100%;
            height: calc(100vh - 70px);
            min-height: calc(100vh - 70px);
            max-height: calc(100vh - 70px);
            aspect-ratio: auto;
            display: flex;
            align-items: stretch;
            justify-content: center;
            box-sizing: border-box;
            padding: 0;
            background: #0a1624;
          }
          .hero-foto-kasten {
            inset: 0;
            width: 100%;
            height: 100%;
            margin: 0;
          }
          .hero-schleier {
            background: linear-gradient(
              to bottom,
              rgba(10, 22, 36, 0.50) 0%,
              rgba(10, 22, 36, 0.22) 24%,
              rgba(10, 22, 36, 0.12) 44%,
              rgba(10, 22, 36, 0.12) 70%,
              rgba(10, 22, 36, 0.24) 88%,
              rgba(10, 22, 36, 0.36) 100%
            );
          }
          .hero .container {
            width: 100%;
            max-width: 1180px;
            height: 100%;
            display: flex;
            flex-direction: column;
            padding: clamp(36px, 6.5vh, 72px) 56px clamp(40px, 7.5vh, 72px);
          }
          .hero-content {
            display: flex;
            flex-direction: column;
            flex: 1;
            min-height: 0;
          }
          .hero-content h1 {
            font-size: clamp(2.15rem, 3.1vw, 2.7rem);
            line-height: 1.22;
            margin: 0 auto 18px;
          }
          .hero-content > p {
            font-size: 1.14rem;
            margin-bottom: 0;
            max-width: none;
          }
          .hero-actions {
            display: flex;
            justify-content: space-between;
            align-items: stretch;
            width: 100%;
            margin: auto auto 0;
            gap: clamp(20px, 3.4vw, 48px);
          }
          .hero-actions .btn {
            flex: 1 1 0;
            padding: 16px 18px;
            min-width: 0;
          }
          .hero-badges {
            width: 100%;
            margin: 16px auto 0;
            justify-content: space-between;
            gap: 14px;
          }
          .hero.pinsel-an .pinsel-pfad {
            animation: none;
            stroke-dashoffset: 0;
          }
          .hero.pinsel-an .pinsel-rest {
            animation: none;
            opacity: 1;
          }
          .hero-pinsel-bild { mask: none; }
        }
        .hero-foto-kasten {
          position: absolute;
          inset: 0;
          z-index: 0;
          overflow: hidden;
        }
        .hero-foto,
        .hero-pinsel-svg {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          display: block;
        }
        .hero-foto {
          object-fit: cover;
          object-position: center center;
          z-index: 0;
        }
        .hero-pinsel-svg { z-index: 0; }
        .pinsel-pfad {
          stroke-dasharray: 1;
          stroke-dashoffset: 1;
        }
        .pinsel-rest { opacity: 0; }
        .hero.pinsel-an .pinsel-pfad {
          animation: pinsel-zieht 2.15s cubic-bezier(0.4, 0, 0.2, 1) 0.05s 1 forwards;
        }
        .hero.pinsel-an .pinsel-rest {
          animation: pinsel-rest 2.15s ease-in 0.05s 1 forwards;
        }
        .hero.pinsel-fertig .hero-pinsel-bild { mask: none; }
        @keyframes pinsel-zieht {
          to { stroke-dashoffset: 0; }
        }
        @keyframes pinsel-rest {
          0%, 78% { opacity: 0; }
          100% { opacity: 1; }
        }
        @keyframes pinsel-rest-voll {
          0%, 50% { opacity: 0; }
          100% { opacity: 1; }
        }
        @keyframes pinsel-rest-handy {
          0%, 42% { opacity: 0; }
          100% { opacity: 1; }
        }
        @media (prefers-reduced-motion: reduce) {
          .hero.pinsel-an .pinsel-pfad { animation: none; stroke-dashoffset: 0; }
          .hero.pinsel-an .pinsel-rest { animation: none; opacity: 1; }
        }
        .hero-schleier {
          position: absolute;
          inset: 0;
          z-index: 1;
          pointer-events: none;
          background: linear-gradient(to bottom, rgba(15,42,70,0.78) 0%, rgba(15,42,70,0.28) 40%, rgba(15,42,70,0.28) 62%, rgba(15,42,70,0.82) 100%);
        }
        .hero .container { position: relative; z-index: 2; }
        .hero-content h1 { text-shadow: 0 2px 18px rgba(0,0,0,0.55); }
        .hero-content > p { text-shadow: 0 1px 12px rgba(0,0,0,0.6); }

        /* Handy: Verlauf haengt am FOTO, nicht an der ganzen Hero-Hoehe.
           Prozent vom ganzen Hero waren auf dem iPhone falsch (Kante),
           weil Schrift dort einen anderen Anteil hat als im Mac-Fenster. */
        @media (max-width: 768px) {
          .hero {
            width: 100%;
            max-width: 100%;
            min-width: 0;
            height: auto;
            max-height: none;
            min-height: 0;
            display: flex;
            flex-direction: column;
            overflow: hidden;
            padding: 26px 0 0;
            background: #12344f;
          }
          .hero-schleier { display: none; }
          .hero .container {
            position: relative;
            z-index: 2;
            flex: 0 0 auto;
            width: 100%;
            max-width: 100%;
            min-width: 0;
          }
          .hero-foto-kasten {
            position: relative;
            order: 2;
            width: 100%;
            height: 78vw;
            max-height: 460px;
            margin: -52px 0 0;
            flex: 0 0 auto;
            overflow: hidden;
          }
          .hero.pinsel-an .pinsel-pfad {
            animation-duration: 1.15s;
          }
          .hero.pinsel-an .pinsel-rest {
            animation-name: pinsel-rest-handy;
            animation-duration: 1.2s;
          }
          .hero-foto-kasten::before {
            content: "";
            display: block;
            position: absolute;
            left: 0;
            right: 0;
            top: 0;
            height: 62%;
            z-index: 1;
            pointer-events: none;
            background: linear-gradient(
              to bottom,
              #12344f 0%,
              #12344f 12%,
              rgba(18,52,79,0.94) 26%,
              rgba(18,52,79,0.72) 40%,
              rgba(18,52,79,0.42) 55%,
              rgba(18,52,79,0.16) 74%,
              rgba(18,52,79,0) 100%
            );
          }
          .hero-foto-kasten::after {
            content: "";
            display: block;
            position: absolute;
            left: 0;
            right: 0;
            bottom: 0;
            height: 28%;
            z-index: 1;
            pointer-events: none;
            background: linear-gradient(
              to bottom,
              rgba(255,255,255,0) 0%,
              rgba(255,255,255,0.12) 38%,
              rgba(255,255,255,0.5) 72%,
              #fff 100%
            );
          }
          .hero-foto,
          .hero-pinsel-svg {
            position: relative;
            inset: auto;
            width: 100%;
            height: 100%;
            max-width: 100%;
            min-width: 0;
          }
          .hero-foto {
            display: block;
            object-fit: cover;
            object-position: center 48%;
            transform: none;
            margin-top: 0;
            -webkit-mask-image: none;
            mask-image: none;
          }
          .hero-content h1 {
            font-size: 1.48rem;
            margin-bottom: 12px;
            overflow-wrap: break-word;
          }
          .hero-content > p {
            font-size: 0.92rem;
            margin-bottom: 28px;
            padding: 0 4px;
          }
          .hero-actions {
            margin-bottom: 28px;
          }
          .hero-actions .btn {
            padding: 14px 28px;
            font-size: 1rem;
          }
        }
        `}
      </style>

      {/* Hero */}
      <section
        id="home"
        className={`hero${pinselAn ? " pinsel-an" : ""}${pinselFertig ? " pinsel-fertig" : ""}`}
      >
        <div className="hero-foto-kasten">
          <svg
            className="hero-pinsel-svg"
            viewBox="0 0 1365 768"
            preserveAspectRatio="xMidYMid slice"
            aria-hidden="true"
          >
            <defs>
              <filter id="pinselWeich" x="-30%" y="-30%" width="160%" height="160%">
                <feGaussianBlur stdDeviation="16" />
              </filter>
              <mask id="pinselMask" maskUnits="userSpaceOnUse">
                <rect width="1365" height="768" fill="#000" />
                {!pinselFertig && (
                  <g filter="url(#pinselWeich)">
                    <path
                      className="pinsel-pfad"
                      d="M 80 540 C 340 200 470 660 720 310 S 1080 640 1380 250"
                      fill="none"
                      stroke="#fff"
                      strokeWidth="460"
                      strokeLinecap="round"
                      pathLength="1"
                    />
                    <path
                      className="pinsel-pfad"
                      d="M 40 690 C 340 800 520 600 760 770 S 1080 620 1400 800"
                      fill="none"
                      stroke="#fff"
                      strokeWidth="360"
                      strokeLinecap="round"
                      pathLength="1"
                    />
                    <path
                      className="pinsel-pfad"
                      d="M 200 620 C 480 700 700 580 980 710 S 1200 640 1365 720"
                      fill="none"
                      stroke="#fff"
                      strokeWidth="280"
                      strokeLinecap="round"
                      pathLength="1"
                    />
                  </g>
                )}
                <rect
                  className="pinsel-rest"
                  width="1365"
                  height="768"
                  fill="#fff"
                  onAnimationEnd={() => {
                    window.setTimeout(() => setPinselFertig(true), 80);
                  }}
                />
              </mask>
            </defs>
            <image
              className="hero-pinsel-bild"
              href="/images/hero-mah3.webp"
              width="1365"
              height="768"
              mask={pinselFertig ? undefined : "url(#pinselMask)"}
            />
          </svg>
          <div className="hero-schleier" aria-hidden="true" />
        </div>
        <div className="container">
          <div className="hero-content">
            <h1>{t("hero.title")}</h1>
            <p>{t("hero.subtitle")}</p>
            <div className="hero-actions">
              <button type="button" className="btn" onClick={() => zumFormular("purchase")}>
                {t("hero.cta")}
              </button>
              {/* Direkter Draht: viele Verkaeufer wollen kurz fragen statt ein
                  langes Formular auszufuellen. Reiner Link, es wird nichts
                  nachgeladen und nichts uebertragen, bis jemand tippt. */}
              <a
                href={whatsappLink(t("hero.whatsapp_text"))}
                className="btn btn-whatsapp"
                target="_blank"
                rel="noopener noreferrer"
              >
                <IconWhatsApp /> {t("hero.cta_whatsapp")}
              </a>
              {/* Die Nummer steht bewusst AUF dem Knopf: am Handy waehlt ein
                  Tipp, am Rechner kann man sie einfach ablesen. */}
              <a href="tel:+4917696084998" className="btn btn-anruf">
                <i className="fas fa-phone"></i> {t("hero.cta_anruf")}
              </a>
            </div>
            {/* Hero 
            <a href="/cars" className="btn">
              Unsere Autos
            </a>
              */}
            <div className="hero-badges">
              <div className="badge">
                <i className="fas fa-check-circle"></i> {t("hero.badge_fair")}
              </div>
              <div className="badge">
                <i className="fas fa-check-circle"></i> {t("hero.badge_fast")}
              </div>
              <div className="badge">
                <i className="fas fa-check-circle"></i> {t("hero.badge_pro")}
              </div>
              <div className="badge">
                <i className="fas fa-check-circle"></i> {t("hero.badge_wish")}
              </div>
            </div>
          </div>
        </div>
      </section>


      {/* Services */}
      <section id="services" className="services">
        <div className="container">
          <h2>{t("services.title")}</h2>
          <div className="services-grid">
            <div className="service-card">
              <div className="service-icon">
                <i className="fas fa-car"></i>
              </div>
              <h3>{t("services.card1.title")}</h3>
              <p>{t("services.card1.text")}</p>
            </div>
            <div className="service-card">
              <div className="service-icon">
                <i className="fas fa-passport"></i>
              </div>
              <h3>{t("services.card2.title")}</h3>
              <p>{t("services.card2.text")}</p>
            </div>
            <div className="service-card">
              <div className="service-icon">
                <i className="fas fa-file-alt"></i>
              </div>
              <h3>{t("services.card3.title")}</h3>
              <p>{t("services.card3.text")}</p>
            </div>
            <div className="service-card">
              <div className="service-icon">
                <i className="fas fa-handshake"></i>
              </div>
              <h3>{t("services.card4.title")}</h3>
              <p>{t("services.card4.text")}</p>
            </div>
            {/* Kachel 5 und 6 sagten dasselbe und wurden zusammengelegt */}
            <div className="service-card">
              <div className="service-icon">
                <i className="fas fa-magnifying-glass-location"></i>
              </div>
              <h3>{t("services.card5.title")}</h3>
              <p>{t("services.card5.text")}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Ablauf in vier Schritten - nimmt dem Verkaeufer die Unsicherheit,
          worauf er sich einlaesst. Schritt 3 spiegelt AGB Paragraf 3:
          verbindlich wird es erst bei der Einigung. */}
      <section id="ablauf" className="ablauf">
        <div className="container">
          <h2>{t("ablauf.title")}</h2>
          <p className="section-subtitle ablauf-sub">{t("ablauf.subtitle")}</p>
          <div className="ablauf-schritte">
            {[1, 2, 3, 4].map((n) => (
              <div className="ablauf-schritt" key={n}>
                <div className="ablauf-nummer">{n}</div>
                <h4>{t(`ablauf.s${n}_title`)}</h4>
                <p>{t(`ablauf.s${n}_text`)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Fahrzeug-Ankauf / Wunschauto-Service */}
      <section id="vehicle-purchase" className="vehicle-purchase">
        <div className="container">
          <h2>{t("purchase.title")}</h2>

          <div className="purchase-intro">
            <p className="section-subtitle">
              {t("purchase.subtitle")}
            </p>
            <h3>{t("purchase.offer_two_services")}</h3>
            {/* Die zwei Karten heben sich beim Darueberfahren an und sehen
                dadurch anklickbar aus. Also sind sie es jetzt auch: ein Klick
                waehlt den passenden Service im Formular und springt hin. */}
            <div className="service-duo">
              <button
                type="button"
                className="service-duo-card"
                onClick={() => aufleuchtenDann("purchase")}
              >
                {/* duo-bild liegt genau auf dem Bild (gleiches Seitenverhaeltnis).
                    Nur so sitzen die Scheinwerfer-Punkte an der richtigen Stelle:
                    das Bild fuellt den quadratischen Rahmen NICHT aus. */}
                <div className={`duo-icon duo-ankauf${leuchtet === "purchase" ? " leuchtet" : ""}`}>
                  <span className="duo-bild">
                    <img src="/images/icon-ankauf-h9.webp" alt="" />
                    <span className="duo-licht licht-nah" aria-hidden="true" />
                    <span className="duo-licht licht-fern" aria-hidden="true" />
                  </span>
                </div>
                <h4>{t("purchase.feature_buy.title")}</h4>
                <p>{t("purchase.feature_buy.text")}</p>
              </button>
              <button
                type="button"
                className="service-duo-card"
                onClick={() => aufleuchtenDann("wish_car")}
              >
                <div className={`duo-icon duo-wunsch${leuchtet === "wish_car" ? " leuchtet" : ""}`}>
                  <span className="duo-bild">
                    <img src="/images/icon-wunsch-h10.webp" alt="" />
                    <span className="duo-licht licht-nah" aria-hidden="true" />
                    <span className="duo-licht licht-fern" aria-hidden="true" />
                  </span>
                </div>
                <h4>{t("purchase.feature_wish.title")}</h4>
                <p>{t("purchase.feature_wish.text")}</p>
              </button>
            </div>
            <p className="accept-note">{t("purchase.feature_note")}</p>
          </div>

          <h2>{t("purchase.accept_all_title")}</h2>

          <div className="purchase-intro">
            <div className="accept-card">
              <div className="accept-grid">
                {[
                  t("purchase.accept_high_mileage"),
                  t("purchase.accept_defects"),
                  t("purchase.accept_accident"),
                  t("purchase.accept_total_loss"),
                  t("purchase.accept_young_old"),
                  t("purchase.accept_luxury_daily"),
                  t("purchase.accept_with_without_history"),
                ].map((txt, idx) => (
                  <span className="accept-row" key={idx}>
                    <i className="fas fa-check"></i>
                    {txt}
                  </span>
                ))}
              </div>
            </div>
            <p className="accept-note">{t("purchase.fair_offer_any_condition")}</p>
            {/* Abschluss des Bereichs: oeffnet dasselbe Fenster wie die Karten */}
            <div className="ankauf-abschluss">
              <button type="button" className="btn" onClick={() => zumFormular("purchase")}>
                {t("carform.btn_submit_purchase")}
              </button>
            </div>
          </div>

          {/* Das Formular selbst steckt jetzt im Fenster weiter unten */}
        </div>
      </section>

      {/* Über mich */}
      <section id="about" className="about">
        <div className="container">
          <h2>{t("about.title")}</h2>
          <div className="about-content">
            <div className="about-image">
              {/* Dreh-Karte: vorne das Foto, hinten die Kontaktdaten.
                  Als <button>, damit sie auch mit der Tastatur bedienbar ist. */}
              {/* KEIN <button> mehr aussen herum: in einem Knopf darf kein Link
                  stehen, der QR-Code soll aber anklickbar sein. Stattdessen
                  klickbare Flaechen plus je ein echter Knopf zum Drehen,
                  damit es auch mit der Tastatur geht. */}
              <div className={`dreh ${gedreht ? "ist-gedreht" : ""}`}>
                <div className="dreh-innen">
                  {[5, 4, 3, 2, 1, 0, -1, -2, -3, -4, -5, -6].map((z) => (
                    <span
                      key={z}
                      className="dreh-kern"
                      style={{ transform: `translateZ(${z}px)` }}
                      aria-hidden="true"
                    />
                  ))}
                  <div className="dreh-vorn" onClick={() => setGedreht(true)}>
                    <img src="/images/profil-bild2.webp" alt={t("about.owner_alt")} />
                    <button
                      type="button"
                      className="dreh-hinweis"
                      onClick={() => setGedreht(true)}
                      aria-label="Kontaktdaten zeigen"
                    >
                      <i className="fas fa-arrows-rotate" aria-hidden="true"></i>
                    </button>
                  </div>

                  <div className="dreh-hinten" onClick={() => setGedreht(false)}>
                    <span className="dreh-firma">AUTO M.A.H.</span>
                    <strong>{t("about.owner_name")}</strong>
                    <span className="dreh-rolle">{t("about.owner_role")}</span>

                    {/* QR fuehrt auf WhatsApp. Sinnvoll NUR am Rechner: dort
                        holt man sich damit den Draht aufs Handy, wo die
                        Fahrzeugfotos liegen. Am Handy waere er nutzlos (man
                        kann den eigenen Bildschirm nicht scannen), darum
                        erscheint dort stattdessen die WhatsApp-Zeile. */}
                    <span className="dreh-zeile">
                      <i className="fas fa-phone" aria-hidden="true"></i> +49 176 96084998
                    </span>
                    <span className="dreh-zeile dreh-mail">
                      <i className="fas fa-envelope" aria-hidden="true"></i> info@auto-mah.com
                    </span>
                    <span className="dreh-zeile">
                      <i className="fas fa-map-marker-alt" aria-hidden="true"></i>
                      Daimlerstr. 10, 79585 Steinen
                    </span>

                    {/* QR als Abschluss ganz unten. stopPropagation, damit ein
                        Klick WhatsApp oeffnet statt die Karte zurueckzudrehen. */}
                    <a
                      href={whatsappLink(t("hero.whatsapp_text"))}
                      className="dreh-qr-link"
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      title="Per WhatsApp schreiben"
                    >
                      <img
                        src="/images/qr-whatsapp2.webp"
                        alt="QR-Code: per WhatsApp schreiben"
                        className="dreh-qr"
                      />
                      <span className="dreh-qr-text">{t("about.qr_text")}</span>
                    </a>

                    {/* Zurueck sitzt als kleines Zeichen oben rechts, damit der
                        QR-Code den unteren Abschluss bilden kann. */}
                    <button
                      type="button"
                      className="dreh-zurueck"
                      onClick={(e) => {
                        e.stopPropagation();
                        setGedreht(false);
                      }}
                      aria-label="Foto zeigen"
                    >
                      <i className="fas fa-rotate-left" aria-hidden="true"></i>
                    </button>
                  </div>
                </div>
              </div>

              <div className="owner-name">
                <h3>{t("about.owner_name")}</h3>
                <p>{t("about.owner_role")}</p>
              </div>

            </div>
            <div className="about-text">
              <h3>{t("about.headline")}</h3>
              <p>{t("about.p1")}</p>
              <p>{t("about.p2")}</p>
              <p>
                <strong>{t("about.new_wish_service_title")}</strong>
                <br />
                {t("about.new_wish_service_text")}
              </p>

              <div className="business-note">
                <p>
                  <strong>{t("about.note_strong")}</strong>{" "}
                  {t("about.note_text")}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Fahrzeuge />

      {/* Haeufige Zoll-Fragen. Zeigt die Fachkenntnis, statt sie zu behaupten.
          Aufklappbar ueber <details> - braucht kein JavaScript und funktioniert
          auch mit Vorlese-Programmen. */}
      <section id="faq" className="faq">
        <div className="container">
          <h2>{t("faq.title")}</h2>
          <p className="section-subtitle faq-sub">{t("faq.subtitle")}</p>
          <div className="faq-liste">
            {[1, 2, 3, 4, 5].map((n) => (
              <details className="faq-punkt" key={n} open={n === 1}>
                <summary>
                  {t(`faq.q${n}`)}
                  <i className="fas fa-chevron-down"></i>
                </summary>
                <p>{t(`faq.a${n}`)}</p>
                {/* Nur bei der Schweiz-Frage: Weg zur ausfuehrlichen Seite.
                    Der Antworttext selbst bleibt unveraendert, weil Google
                    ihn woertlich mit dem Datenblock vergleicht. */}
                {n === 5 && (
                  <Link to="/auto-verkaufen-schweiz" className="faq-mehr">
                    {t("faq.a5_link")}
                  </Link>
                )}
              </details>
            ))}
          </div>
          <p className="faq-hinweis">{t("faq.hinweis")}</p>
        </div>
      </section>

      {/* Kontakt */}
      <section id="contact" className="contact">
        <div className="container">
          <h2>{t("contact.title")}</h2>
          <div className="contact-content">
            <div className="contact-info">
              <h3>{t("contact.brand")}</h3>
              <p>
                <i className="fas fa-phone"></i>{" "}
                <a
                  href="tel:+4917696084998"
                  style={{ color: "#fff", textDecoration: "none" }}
                >
                  +49 176 96084998
                </a>
              </p>

              <p>
                <i className="fas fa-envelope"></i>{" "}
                <a
                  href="mailto:info@auto-mah.com"
                  style={{ color: "#fff", textDecoration: "none" }}
                >
                  info@auto-mah.com
                </a>
              </p>

              <p>
                <i className="fas fa-map-marker-alt"></i> Daimlerstr. 10, 79585 Steinen, DE
              </p>

              {/* Erreichbarkeit. Stand vorher nirgends auf der Seite: wer sein
                  Auto verkaufen will, sieht drei Knoepfe und weiss nicht, ob er
                  ueberhaupt stoert. Im Zweifel meldet er sich dann gar nicht.
                  BEWUSST KEINE Uhrzeiten: feste Oeffnungszeiten gibt es hier
                  nicht, eine erfundene Zeit waere schlechter als gar keine. */}
              <p className="kontakt-zeiten">
                <i className="fas fa-clock"></i>{" "}
                <span>
                  <strong>{t("contact.hours_title")}</strong>
                  <br />
                  {t("contact.hours_main")}
                </span>
              </p>
              <div className="kontakt-karte">
                <a
                  className="kontakt-karte-bild"
                  href="https://www.google.com/maps/dir/?api=1&destination=Daimlerstra%C3%9Fe+10%2C+79585+Steinen&travelmode=driving"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <img
                    src="/images/standort-karte.svg"
                    alt={t("contact.map_title")}
                    width="1400"
                    height="900"
                  />
                </a>
              </div>

              {/* Einzugsgebiet. BEWUSST nur Landkreise, Kantone und Regionen,
                  KEINE einzelnen Ortschaften: kleine Orte verraten der
                  Konkurrenz, wo wirklich eingekauft wird. */}
              <div className="kontakt-gebiet">
                <h4>{t("contact.area_title")}</h4>
                <p>{t("contact.area_lead")}</p>
                {/* Nach Laendern gruppiert. Vorher standen alle neun Gebiete in
                    einer durchlaufenden Reihe: das Elsass sass zwischen den
                    deutschen Landkreisen und den Schweizer Kantonen, und die
                    Zeilen endeten unterschiedlich weit rechts. Sah zufaellig aus. */}
                <ul className="gebiet-laender">
                  {(t("contact.area_regions") || []).map((g) => (
                    <li key={g.land}>
                      <span className="gebiet-land">{g.land}</span>
                      <span className="gebiet-orte">
                        {g.gebiete.map((o, i) => (
                          <React.Fragment key={o}>
                            {/* Das Trennzeichen steht als EIGENER Text zwischen den
                                Namen, nicht angeklebt. Nur dadurch gibt es ueberhaupt
                                eine Stelle zum Umbrechen. Klebte es am Namen, waere die
                                ganze Zeile ein einziger unteilbarer Block und wuerde
                                auf dem Handy seitlich aus dem Bild laufen. */}
                            {i > 0 && " · "}
                            <span className="gebiet-ort">{o}</span>
                          </React.Fragment>
                        ))}
                      </span>
                    </li>
                  ))}
                </ul>
                {/* Verweis auf die Regionsseite. Zaehlt doppelt: der Besucher
                    aus der Schweiz kommt zum passenden Text, und Google
                    findet die Unterseite ueberhaupt erst ueber solche
                    Verweise von der eigenen Seite. */}
                <Link to="/auto-verkaufen-schweiz" className="gebiet-mehr">
                  {t("contact.area_ch_link")}
                </Link>
              </div>
            </div>

            {/* Rechte Spalte: Kontaktformular */}
            <GeneralForm />
          </div>
        </div>
      </section>

      {/* Ankauf-/Wunschauto-Formular als Fenster ueber der Seite */}
      <Overlay
        offen={formularOffen !== null}
        titel={
          formularOffen === "wish_car"
            ? t("carform.service_wish")
            : t("carform.service_purchase")
        }
        onSchliessen={() => setFormularOffen(null)}
      >
        <CarForm
          wunsch={
            formularOffen
              ? { typ: formularOffen, auto: anfrageAuto, stempel: Date.now() }
              : null
          }
          onFertig={() => setFormularOffen(null)}
        />
      </Overlay>
    </>
  );
}
